import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { registerServiceWorker } from './lib/sw-register';
import { resyncPushSubscription } from './lib/push';
import { bootstrapDefaultServers } from './lib/bootstrap-servers';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element missing in index.html');

// Sprint 11b post-flip catch-22 fix — pre-seed the multi-server registry
// from the public manifest BEFORE first render, so `getActiveBaseUrl()`
// returns the canonical backend URL on the very first `/api/auth/me`
// call. ~50-100 ms in the common case (same-origin static asset);
// hard-capped at 2 s by the helper so a network blackhole degrades
// gracefully instead of hanging the boot indefinitely. Wrapped in an
// async IIFE because top-level await would push the build target above
// es2020 (current floor).
void (async () => {
  await bootstrapDefaultServers();

  createRoot(rootEl).render(
    <StrictMode>
      {/* S7.4 — outermost ErrorBoundary. Without this, ANY uncaught render
          exception unmounts the whole tree and the founder sees a black
          page with no recovery affordance. */}
      <ErrorBoundary surface="root">
        {/* `basename` sale de la base del build: en producción es '/' y en un
            despliegue de PRUEBAS servido bajo un prefijo (p. ej. /oficina-v3/)
            es ese prefijo, sin tocar una sola ruta del código. */}
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </StrictMode>,
  );

  // 21-09-2026 (infra-ops, PUSH A PAU v1.1): se registra el SW MINIMO de
  // public/sw.js (solo `push` + `notificationclick`; sin `fetch`, sin caches)
  // — es lo que permite que un mensaje del COO le vibre a Pau en el movil.
  // Antes (18-09) aqui se DESREGISTRABA todo SW: el de workbox precacheaba el
  // bundle y servia "siempre la oficina vieja" (Pau). Esa proteccion se
  // conserva: se borran las caches viejas antes de registrar y el SW nuevo no
  // intercepta ninguna peticion, asi que index.html sigue llegando del
  // servidor (no-store) en cada navegacion.
  void registerServiceWorker().then(() => resyncPushSubscription());
})();
